/**
 * AppointmentConfirmationFormModal — the corp dashboard's Add / Edit
 * Confirmation dialog (components/appointment-confirmations-table.tsx), for
 * one account, copied as it is:
 *   - Details / Email / SMS tabs under the title, "modified" on Email and SMS
 *     when their text changed;
 *   - Details: Name *, Send Type (Email / SMS / Email & SMS), the Schedule
 *     (a checkbox under each of the eight days, 7 days before to the day of
 *     the appointment), then When Confirmed and On Appt. Creation side by side;
 *   - Email: the body in the HTML editor, with Insert Shortcode;
 *   - SMS: Insert Shortcode at the caret, the message, and its length out of
 *     1600 characters;
 *   - corp's checks: Name, and the email or SMS text the Send Type sends, are
 *     required; no schedule day is a suggestion.
 * Corp's Accounts picker (create in several accounts) is a multi-account tool,
 * so it is not here: a confirmation is added to this account. The booking-text
 * override is not on corp's form either, and saving never touches it.
 * On a Locked page AdminFormModal makes it a viewer; the tabs still switch and
 * Insert Shortcode is gone.
 */
import { useRef, useState } from 'react';
import { Braces } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import { cn } from '@/lib/utils';
import {
  createAppointmentConfirmation,
  updateAppointmentConfirmation,
  type AppointmentConfirmation,
  type AppointmentConfirmationPayload,
} from '@/services/admin.service';
import { getErrorMessage } from '@/services/apiClient';
import { toast } from '@/store/toastStore';
import { AdminFormModal } from './AdminFormModal';
import { useAdminPageLevel } from './AdminPageLevelContext';
import { DialogField, LABEL_CLASS, ModifiedPill, fieldClasses, type FieldState } from './DialogChrome';
import { RichTextEditor } from './RichTextEditor';
import { ShortcodeDialog } from './ShortcodeDialog';
import { useTrackedForm } from './useTrackedForm';

type Form = AppointmentConfirmationPayload;
type DayKey = 'day1' | 'day2' | 'day3' | 'day4' | 'day5' | 'day6' | 'day7' | 'day8';
type Tab = 'details' | 'email' | 'sms';

/** Corp's order: furthest out first. */
export const CONFIRMATION_DAYS: { key: DayKey; label: string }[] = [
  { key: 'day8', label: '7 Days Before' },
  { key: 'day7', label: '6 Days Before' },
  { key: 'day6', label: '5 Days Before' },
  { key: 'day5', label: '4 Days Before' },
  { key: 'day4', label: '3 Days Before' },
  { key: 'day3', label: '2 Days Before' },
  { key: 'day2', label: '1 Day Before' },
  { key: 'day1', label: 'Day of Appt' },
];

/** Corp's order in the picker: Email first. */
const SEND_TYPE_OPTIONS = [
  { value: 1, label: 'Email' },
  { value: 0, label: 'SMS' },
  { value: 2, label: 'Email & SMS' },
];
const WHEN_CONFIRMED_OPTIONS = [
  { value: '0', label: 'Stop sending' },
  { value: '1', label: 'Continue sending' },
];
const ON_CREATION_OPTIONS = [
  { value: '0', label: 'Do nothing' },
  { value: '1', label: 'Prompt to confirm' },
];

const SMS_LIMIT = 1600;

const sendsEmail = (sendType: number) => sendType === 1 || sendType === 2;
const sendsSms = (sendType: number) => sendType === 0 || sendType === 2;

/** Text left once the HTML tags are taken out: corp's "Email content is required" test. */
function htmlText(html: string): string {
  return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
}

function formFrom(c: AppointmentConfirmation | null): Form {
  return {
    name: c?.name ?? '',
    message: c?.message ?? '',
    sms_message: c?.sms_message ?? '',
    // A new one starts as Email, as corp's.
    send_type: c?.send_type ?? 1,
    day1: c?.day1 ?? false,
    day2: c?.day2 ?? false,
    day3: c?.day3 ?? false,
    day4: c?.day4 ?? false,
    day5: c?.day5 ?? false,
    day6: c?.day6 ?? false,
    day7: c?.day7 ?? false,
    day8: c?.day8 ?? false,
    continue_confirm: c?.continue_confirm ?? false,
    prompt_confirm: c?.prompt_confirm ?? false,
  };
}

/** Corp's required checks. */
export function confirmationErrors(f: Form): string[] {
  const errors: string[] = [];
  if (!f.name.trim()) errors.push('Name is required');
  if (sendsEmail(f.send_type) && !htmlText(f.message)) errors.push('Email content is required');
  if (sendsSms(f.send_type) && !f.sms_message.trim()) errors.push('SMS content is required');
  return errors;
}

export function confirmationSuggestions(f: Form): string[] {
  return CONFIRMATION_DAYS.some((d) => f[d.key]) ? [] : ['No schedule days selected'];
}

export interface AppointmentConfirmationFormModalProps {
  /** Null to add one. */
  confirmation: AppointmentConfirmation | null;
  onClose: () => void;
  onSaved: () => void;
}

export function AppointmentConfirmationFormModal({ confirmation, onClose, onSaved }: AppointmentConfirmationFormModalProps) {
  const isNew = confirmation === null;
  const locked = useAdminPageLevel() === 'locked';
  const t = useTrackedForm<Form>(() => formFrom(confirmation), { tracking: !isNew });
  const { form } = t;
  const [tab, setTab] = useState<Tab>('details');
  const [saving, setSaving] = useState(false);
  const [shortcodeOpen, setShortcodeOpen] = useState(false);
  const smsRef = useRef<HTMLTextAreaElement | null>(null);

  const errors = confirmationErrors(form);
  const suggestions = confirmationSuggestions(form);
  const missingName = !form.name.trim();
  const nameState: FieldState = missingName ? 'error' : 'none';
  const scheduleChanged = CONFIRMATION_DAYS.some((d) => t.changed.has(d.key));

  /** Insert Shortcode: the code and a space at the caret, the caret after it (as corp). */
  function insertIntoSms(code: string) {
    const el = smsRef.current;
    const start = el?.selectionStart ?? form.sms_message.length;
    const end = el?.selectionEnd ?? form.sms_message.length;
    const text = `${code} `;
    t.patch({ sms_message: form.sms_message.slice(0, start) + text + form.sms_message.slice(end) });
    requestAnimationFrame(() => {
      if (!el) return;
      el.focus();
      el.setSelectionRange(start + text.length, start + text.length);
    });
  }

  async function handleSave() {
    if (errors.length > 0 || saving) return;
    setSaving(true);
    try {
      if (confirmation) {
        await updateAppointmentConfirmation(confirmation.appointmentConfirmTypeID, form);
        toast.success(`"${form.name}" updated`);
      } else {
        await createAppointmentConfirmation(form);
        toast.success(`"${form.name}" created`);
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(getErrorMessage(err, 'The confirmation could not be saved.'));
    } finally {
      setSaving(false);
    }
  }

  const tabButton = (id: Tab, label: string, changed?: { key: keyof Form; label: string }) => (
    <div className="flex items-center gap-1">
      <button
        type="button"
        role="tab"
        aria-selected={tab === id}
        onClick={() => setTab(id)}
        className={cn(
          'rounded-md px-4 py-1.5 text-sm font-medium transition-colors',
          tab === id ? 'bg-primary text-white' : 'text-slate-500 hover:bg-slate-100',
        )}
      >
        {label}
      </button>
      {changed && t.badge(changed.key, changed.label)}
    </div>
  );

  return (
    <AdminFormModal
      title={isNew ? 'Add Confirmation' : 'Edit Confirmation'}
      isNew={isNew}
      dirty={t.dirty}
      saving={saving}
      errors={errors}
      suggestions={suggestions}
      saveLabel={isNew ? 'Create' : 'Save'}
      onSave={() => void handleSave()}
      onRevertAll={t.revertAll}
      onClose={onClose}
      size="xl"
      panelClassName="md:max-w-[960px] md:h-[85vh] md:max-h-[85vh]"
      pinned={
        <div className="flex flex-wrap gap-1" role="tablist" aria-label="Confirmation sections">
          {tabButton('details', 'Details')}
          {tabButton('email', 'Email', { key: 'message', label: 'Email' })}
          {tabButton('sms', 'SMS', { key: 'sms_message', label: 'SMS' })}
        </div>
      }
    >
      {tab === 'details' && (
        <div className="space-y-6">
          <DialogField
            label="Name"
            htmlFor="confirmation-name"
            required
            state={nameState}
            labelHint={missingName ? '— required' : undefined}
            badge={t.badge('name', 'Name')}
          >
            <Input
              id="confirmation-name"
              value={form.name}
              placeholder="Confirmation name"
              onChange={(e) => t.patch({ name: e.target.value })}
              className={fieldClasses.input(nameState)}
            />
          </DialogField>

          <DialogField label="Send Type" htmlFor="confirmation-send-type" badge={t.badge('send_type', 'Send Type')}>
            <Select<number>
              id="confirmation-send-type"
              options={SEND_TYPE_OPTIONS}
              value={form.send_type}
              onChange={(v) => v != null && t.patch({ send_type: v })}
            />
          </DialogField>

          <div>
            <div className="flex flex-wrap items-center gap-2">
              <span className={LABEL_CLASS}>Schedule</span>
              {!isNew && scheduleChanged && (
                <ModifiedPill
                  label="Schedule"
                  onRevert={() => t.patch(Object.fromEntries(CONFIRMATION_DAYS.map((d) => [d.key, t.saved[d.key]])) as Partial<Form>)}
                />
              )}
            </div>
            <div className="mt-2 grid grid-cols-1 gap-3 min-[360px]:grid-cols-4 sm:grid-cols-8" data-testid="confirmation-schedule">
              {CONFIRMATION_DAYS.map(({ key, label }) => (
                <label key={key} htmlFor={`confirmation-${key}`} className="flex cursor-pointer flex-col items-center gap-1">
                  <span className="text-center text-[11px] text-slate-500">{label}</span>
                  <Checkbox
                    id={`confirmation-${key}`}
                    aria-label={label}
                    checked={form[key]}
                    onChange={(e) => t.patch({ [key]: e.target.checked } as Partial<Form>)}
                  />
                </label>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <DialogField label="When Confirmed" htmlFor="confirmation-when-confirmed" badge={t.badge('continue_confirm', 'When Confirmed')}>
              <Select<string>
                id="confirmation-when-confirmed"
                options={WHEN_CONFIRMED_OPTIONS}
                value={form.continue_confirm ? '1' : '0'}
                onChange={(v) => t.patch({ continue_confirm: v === '1' })}
              />
            </DialogField>
            <DialogField label="On Appt. Creation" htmlFor="confirmation-on-creation" badge={t.badge('prompt_confirm', 'On Appt. Creation')}>
              <Select<string>
                id="confirmation-on-creation"
                options={ON_CREATION_OPTIONS}
                value={form.prompt_confirm ? '1' : '0'}
                onChange={(v) => t.patch({ prompt_confirm: v === '1' })}
              />
            </DialogField>
          </div>
        </div>
      )}

      {tab === 'email' && (
        <RichTextEditor
          id="confirmation-email"
          shortcodes
          value={form.message}
          onChange={(html) => t.patch({ message: html })}
          invalid={sendsEmail(form.send_type) && !htmlText(form.message)}
          placeholder="Email body..."
        />
      )}

      {tab === 'sms' && (
        <div className="flex flex-col gap-2">
          {!locked && (
            <div className="flex justify-end">
              <Button variant="secondary" size="xs" leftIcon={<Braces className="h-3.5 w-3.5" />} onClick={() => setShortcodeOpen(true)}>
                Insert Shortcode
              </Button>
            </div>
          )}
          <Textarea
            id="confirmation-sms"
            aria-label="SMS message"
            ref={smsRef}
            rows={14}
            value={form.sms_message}
            placeholder="SMS message text..."
            onChange={(e) => t.patch({ sms_message: e.target.value })}
            className={cn('resize-none', fieldClasses.input(sendsSms(form.send_type) && !form.sms_message.trim() ? 'error' : 'none'))}
          />
          <p className="text-xs text-slate-400">
            {form.sms_message.length} / {SMS_LIMIT} characters
          </p>
        </div>
      )}

      {shortcodeOpen && (
        <ShortcodeDialog
          onClose={() => setShortcodeOpen(false)}
          onInsert={(code) => {
            insertIntoSms(code);
            setShortcodeOpen(false);
          }}
        />
      )}
    </AdminFormModal>
  );
}
